import React from 'react';
import { Button, HStack, Text, VStack } from '@chakra-ui/react';
import type { CategoryItem } from '../types';
import { streamOutlineOnDarkHover } from '../darkThemeButtonProps';
import { SearchBar } from './SearchBar';

interface Props {
  categories: CategoryItem[];
  selectedId: string;
  onSelect: (id: string) => void;
  label?: string;
  /** Tighter pills for dense layouts */
  compact?: boolean;
  categorySearch?: string;
  onCategorySearchChange?: (q: string) => void;
}

export function CategoryRail({
  categories,
  selectedId,
  onSelect,
  label,
  compact,
  categorySearch = '',
  onCategorySearchChange,
}: Props) {
  const allId = 'all';
  const showAll = categories.length > 1 || categories.length === 0;
  const sz = compact ? 'xs' : 'sm';

  return (
    <VStack align="stretch" spacing={compact ? 1 : 2}>
      {onCategorySearchChange && (
        <SearchBar
          compact
          value={categorySearch}
          onChange={onCategorySearchChange}
          placeholder="Categories (2+ chars)"
        />
      )}
      {label && (
        <Text
          fontSize={compact ? '10px' : 'xs'}
          fontWeight="700"
          color="whiteAlpha.500"
          textTransform="uppercase"
          letterSpacing="0.08em"
          mb={0}
        >
          {label}
        </Text>
      )}
      <HStack
        spacing={compact ? 1 : 2}
        overflowX="auto"
        pb={compact ? 1 : 2}
        sx={{
          '&::-webkit-scrollbar': { height: '6px' },
          '&::-webkit-scrollbar-thumb': { bg: 'whiteAlpha.300', borderRadius: 'full' },
        }}
      >
        {showAll && (
          <Button
            size={sz}
            h={compact ? '24px' : undefined}
            borderRadius="full"
            variant={selectedId === allId ? 'solid' : 'outline'}
            colorScheme="teal"
            color={selectedId === allId ? 'gray.900' : 'whiteAlpha.800'}
            borderColor="whiteAlpha.300"
            {...(selectedId !== allId ? streamOutlineOnDarkHover : {})}
            onClick={() => onSelect(allId)}
            flexShrink={0}
          >
            All
          </Button>
        )}
        {categories.map((c) => (
          <Button
            key={c.id}
            size={sz}
            h={compact ? '24px' : undefined}
            borderRadius="full"
            variant={selectedId === c.id ? 'solid' : 'outline'}
            colorScheme="teal"
            color={selectedId === c.id ? 'gray.900' : 'whiteAlpha.800'}
            borderColor="whiteAlpha.300"
            {...(selectedId !== c.id ? streamOutlineOnDarkHover : {})}
            onClick={() => onSelect(c.id)}
            flexShrink={0}
            maxW={compact ? '140px' : undefined}
          >
            <Text noOfLines={1}>{c.name}</Text>
          </Button>
        ))}
      </HStack>
    </VStack>
  );
}
